import React from "react";
import {
	SEGMENT_CMC,
	URL_CMC_AIRCRAFT_DATA,
	URL_CMC_AIRCRAFT_HISTORY_DATA,
	URL_CMC_AIRCRAFT_OWNERSHIP_DATA,
	URL_CMC_AIRFRAME_MRO_PROVIDER_DATA,
	URL_DATA_FEEDS,
} from "../constants";
import { Hero } from "../sections/hero/Hero";
import { Technology } from "../sections/Technology";
import { Testimonials } from "../../common/layout/testimonials/Testimonials";
import { Upgrades } from "../sections/Upgrades";
import { ProductTabs } from "../sections/ProductTabs";
import { ProductSlider } from "../sections/ProductSlider";
import { Trial } from "../sections/Trial";
import { Pricing } from "../sections/pricing/Pricing";
import { Faq } from "../sections/faq/Faq";
import { ProudToServe } from "../sections/proud-to-serve/ProudToServe";
import { productPageProps } from "../../common/types/product";

export default function AirframeMroEventData({
	packageName,
	subscribeLink,
	testimonials,
}: productPageProps) {
	return (
		<>
			<Hero
				title={packageName}
				subtitle="Get insights into over 50,000 maintenance events with detailed information on location, date, MRO event type, and duration of each."
				packages={[
					{
						label: SEGMENT_CMC,
						features: [
							"Gain info on 50,000+ commercial airframe MRO events, including location, date, type, and duration",
							"Powered by Spire ADS-B data and ch-aviation’s MRO provider and contract database",
						],
						subscribeLinkUrl: subscribeLink,
					},
				]}
			/>
			<ProudToServe />
			<ProductTabs
				title={packageName}
				tabs={[
					{
						title: "Airframe MRO Event Data",
						description:
							"Get an overview of airframe MRO events the MRO provider, operator, airport  or registration level, including:",
						list: [
							"Date in/out",
							"Operator name",
							"Airport name",
							"Airframe MRO Name",
							"Aircraft variant",
							"Aircraft registration",
						],
						visual: {
							url: "/static/img/product/commercial-airframe-mro-event-data/mro-provider-profile-events.jpg",
						},
					},
				]}
			/>
			<ProductSlider
				description="Search for airframe MRO events by location, operator, MRO provider, event type, aircraft model, engine type and more."
				slides={[
					{
						title: "MRO Event Location",
						description:
							"Search for airframe MRO events by location:",
						list: [
							"Continent",
							"Country",
							"Subdivision",
							"Metro group",
							"Airport name",
							"Operator region",
							"Aircraft register",
						],
						visual: {
							url: "/static/img/product/commercial-airframe-mro-event-data/mro-event-search-by-location.jpg",
							title: "List of all airframe MRO events that took place in Brazil in 2024",
						},
					},
					{
						title: "Operator",
						description: "Search for airframe MRO events by:",
						list: [
							"Operator name or code",
							"Operator IATA code",
							"Operator ICAO code",
						],
						visual: {
							url: "/static/img/product/commercial-airframe-mro-event-data/mro-event-search-by-operator.jpg",
							title: "2025 Maintenance events for Turkish Airlines",
						},
					},
					{
						title: "Airframe MRO",
						description: "Search for airframe MRO events by:",
						list: ["Airframe MRO provider name or code"],
						visual: {
							url: "/static/img/product/commercial-airframe-mro-event-data/mro-event-search-by-mro-name.jpg",
							title: "List of all airframe MRO events provided by Turkish Technic",
						},
					},
					{
						title: "Event",
						description: "Search for airframe MRO events by:",
						list: [
							"Date",
							"Number of days (event duration)",
							"Event type (base, conversion, line + A, etc.)",
						],
						visual: {
							url: "/static/img/product/commercial-airframe-mro-event-data/mro-event-search-by-event.jpg",
							title: "List of all airframe painting events completed in Q4 2024",
						},
					},
					{
						title: "Aircraft Type",
						description: "Search for airframe MRO events by:",
						list: [
							"Aircraft market group",
							"Aircraft manufacturer",
							"Aircraft",
							"Aircraft family",
							"Aircraft variant",
						],
						visual: {
							url: "/static/img/product/commercial-airframe-mro-event-data/mro-event-search-by-aircraft-type.jpg",
							title: "List of A330 airframe MRO events in Q1/2025",
						},
					},
					{
						title: "Aircraft",
						description: "Search for airframe MRO events by:",
						list: [
							"Aircraft registration",
							"Aircraft line/serial number",
						],
						visual: {
							url: "/static/img/product/commercial-airframe-mro-event-data/mro-event-search-by-aircraft-type.jpg",
							title: "List of airframe MRO events for G-DBCJ",
						},
					},
					{
						title: "Engine",
						description: "Search for airframe MRO events by:",
						list: [
							"Engine manufacturer",
							"Engine family",
							"Engine type/subtype",
							"Engine master series",
						],
						visual: {
							url: "/static/img/product/commercial-airframe-mro-event-data/mro-event-search-by-engine.jpg",
							title: "Airframe MRO events lasting over 50 days for aircraft powered by LEAP engines",
						},
					},
				]}
			/>
			<Upgrades
				packageName={packageName}
				modules={[
					{
						title: "Airframe MRO Provider Data",
						content:
							"Get access to MRO provider profiles, list of customers they serve, where their facilities are located, and the type of airframe maintenance they provide.",
						url: URL_CMC_AIRFRAME_MRO_PROVIDER_DATA,
					},
					{
						title: "Aircraft Data",
						content:
							"Get access to our extensive fleet database with over 82,000 aircraft currently being tracked .",
						url: URL_CMC_AIRCRAFT_DATA,
					},
					{
						title: "Aircraft History Data",
						content:
							"Get access to full historical operator data down to an aircraft level.",
						url: URL_CMC_AIRCRAFT_HISTORY_DATA,
					},
					{
						title: "Aircraft Ownership Data",
						content:
							"Get insights into aircraft ownership, management, valuation estimates, full lessor portfolios, and SPV-financed aircraft details.",
						url: URL_CMC_AIRCRAFT_OWNERSHIP_DATA,
					},
				]}
			>
				<Trial subscribeLink={subscribeLink} />
			</Upgrades>
			<Testimonials testimonials={testimonials} />
			<Technology />
			<Pricing
				packageName={packageName}
				description="Gain a comprehensive understanding of the aviation industry with detailed, up-to-date insights."
				list={[
					"Gain info on 50,000+ commercial airframe MRO events, including location, date, type, and duration",
					"Powered by Spire ADS-B data and ch-aviation’s MRO provider and contract database",
				]}
				subscribeLink={subscribeLink}
				downloadsDescription="Our Commercial Aviation Airframe MRO Event Data is ready for instant, unlimited downloads either as Excel, CSV or JSON file."
			>
				<Trial subscribeLink={subscribeLink} />
			</Pricing>
			<Faq
				items={[
					{
						title: "Can I download this data set?",
						content:
							"Commercial Aviation Airframe MRO Event Data includes an optional feature available for purchase that allows unlimited downloads in CSV, XLS, or JSON formats. To learn more about data downloads, please contact our sales team.",
						linkUrl: "mailto:sales@ch-aviation.com",
					},
					{
						title: "Can I integrate this data as a data feed into my own system?",
						content: (
							<p>
								Yes, Commercial Aviation Airframe MRO Event Data
								can be purchased as a data feed for seamless
								integration into your own system. ch-aviation
								also provides full support for the integration
								process. To learn more about data feeds, click{" "}
								<a
									href={URL_DATA_FEEDS}
									target="_blank"
									rel="noopener noreferrer"
								>
									here
								</a>
								.
							</p>
						),
					},
					{
						title: "How frequently is the Airframe MRO Event Data updated?",
						content: "The dataset is updated on a weekly basis.",
					},
					{
						title: "What is the starting point for maintenance event tracking in the Airframe MRO Event?",
						content:
							"The dataset covers maintenance events from July 2024 to the present.",
					},
				]}
			/>
		</>
	);
}
